import { Component, Input, Output, EventEmitter, inject, signal } from '@angular/core';
import { AiChatService } from '../ai-chat/ai-chat.service';
import { ChatMessage, AiStateContext, AppUser } from '../ai-chat/ai-chat.model';
import { SessionStorageService } from '../services/session-storage.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DrawerModule } from 'primeng/drawer';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { Router } from '@angular/router';
import { AiChatHtmlFormatterService } from './ai-chat-html-formatter.service';

@Component({
    selector: 'app-ai-chat-drawer',
    templateUrl: './ai-chat-drawer.component.html',
    styleUrls: ['./ai-chat-drawer.component.css'],
    imports: [
        CommonModule,
        FormsModule,
        DrawerModule,
        ButtonModule,
        InputTextModule,
        ProgressBarModule
    ],
    standalone: true
})
export class AiChatDrawerComponent {

    private aiChatService = inject(AiChatService);
    private sessionStorageService = inject(SessionStorageService);
    private router = inject(Router);
    private htmlFormatter = inject(AiChatHtmlFormatterService);

    @Input() visible = false;
    @Input() stateContext: AiStateContext = {};
    @Output() visibleChange = new EventEmitter<boolean>();

    messages = signal<ChatMessage[]>([]);
    newMessage = signal<string>('');
    isLoading = signal<boolean>(false);

    currentUser: AppUser = { userRole: 'CUSTOMER' };
    botUser: AppUser = { userRole: 'AI_AGENT', fullName: 'AI Assistant' };

    readonly quickPrompts = [
        { label: 'How do I use this page?', text: 'How do I use this page?' },
        { label: 'Show page guide', text: 'Show page guide' },
        { label: 'What can I do here?', text: 'What can I do here?' }
    ];

    private customerId: number = 0;
    private companyId: number = 0;
    private initialized = false;

    private initUser(): void {
        if (this.initialized) return;
        const customer = this.sessionStorageService.getCustomer();
        const company = this.sessionStorageService.getCompany();
        if (customer) {
            this.customerId = customer.id;
            this.currentUser = {
                ...this.currentUser,
                fullName: (customer as any).fullName || (customer as any).name,
                email: (customer as any).email,
                mobileNumber: (customer as any).mobileNumber,
                firebaseId: (customer as any).firebaseId
            };
        }
        if (company) this.companyId = company.id;

        this.messages.set([]);
        this.initialized = true;
    }

    open(): void {
        this.visible = true;
        this.visibleChange.emit(true);
        this.initUser();
    }

    close(): void {
        this.visible = false;
        this.visibleChange.emit(false);
    }

    onVisibleChange(value: boolean): void {
        this.visible = value;
        this.visibleChange.emit(value);
        if (value) {
            this.initUser();
        }
    }

    sendMessage(text?: string): void {
        const messageText = (text || this.newMessage().trim());
        if (!messageText) return;

        const chatMessage: ChatMessage = {
            sender: this.currentUser,
            textContent: messageText,
            timestamp: new Date(),
            customerId: this.customerId,
            companyId: this.companyId,
            stateContext: this.stateContext
        };

        this.messages.update(prev => [...prev, chatMessage]);
        this.newMessage.set('');
        this.isLoading.set(true);

        this.aiChatService.sendMessage(chatMessage).subscribe({
            next: (response) => {
                this.messages.update(prev => [...prev, response]);
                this.isLoading.set(false);
            },
            error: (error) => {
                console.error('AI Chat drawer error:', error);
                this.messages.update(prev => [...prev, {
                    sender: this.botUser,
                    textContent: 'Sorry, I encountered an error. Please try again.',
                    timestamp: new Date()
                }]);
                this.isLoading.set(false);
            }
        });
    }

    openFullChat(): void {
        this.visible = false;
        this.router.navigate(['/ai-chat']);
    }

    convertToHtml(rawText: string, hasStructuredSources = false) {
        return this.htmlFormatter.convertToHtml(rawText, hasStructuredSources);
    }
}
